import {
  MarketAccessory,
  MarketAccordionItem,
  MarketButton,
  MarketPill,
  MarketRow,
} from '@market/react';
import { useTranslation } from 'react-i18next';
import {
  type InstantAccessAccordionItem,
  type InstantAccessRequirementItem,
  InstantAccessRequirementItem_Type,
} from '@squareup/shared-types-protos/squareup/inflows/service/external_instant_access_service';
import { PillDescription_PillVariant } from '@squareup/shared-types-protos/squareup/bbfrontend/common/component/component';
import { withI18n } from '@squareup/service-i18n/components';
import useResponsiveBreakpoint from '../../hooks/useResponsiveBreakpoint';
import BankSvg from '../../assets/svgs/bank';
import DualRotatingArrowSvg from '../../assets/svgs/dual-rotating-arrow';
import SignDollarArrowSvg from '../../assets/svgs/sign-dollar-arrow';
import styles from './ChecklistAccordion.module.scss';

type MarketPillVariant = 'emphasis' | 'normal' | 'success' | 'warning' | 'critical' | 'insight' | 'alpha' | 'beta' | undefined;

export interface ChecklistAccordionProps {
  instantAccessAccordionItems: InstantAccessAccordionItem[];
  onRequirementItemClick: (
    requirementItem: InstantAccessRequirementItem,
    accordionItem: InstantAccessAccordionItem
  ) => void;
}

export const ChecklistAccordion = ({
  instantAccessAccordionItems,
  onRequirementItemClick,
}: ChecklistAccordionProps) => {
  const { t } = useTranslation();
  const isMobile = useResponsiveBreakpoint();

  const getPillVariantAndLabel = (pillVariant?: PillDescription_PillVariant) => {
    switch (pillVariant) {
      case PillDescription_PillVariant.PILL_VARIANT_EMPHASIS:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.pillLabels.getStarted'),
          variant: 'emphasis',
        };
      case PillDescription_PillVariant.PILL_VARIANT_NORMAL:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.pillLabels.comingSoon'),
          variant: 'normal',
        };
      case PillDescription_PillVariant.PILL_VARIANT_SUCCESS:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.pillLabels.active'),
          variant: 'success',
        };
      case PillDescription_PillVariant.PILL_VARIANT_WARNING:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.pillLabels.finishSetUp'),
          variant: 'warning',
        };
      default:
        return {};
    }
  };

  const getRequirementItemLabelAndCTALabel = (requirementItemType?: InstantAccessRequirementItem_Type, platformLabel?: string) => {
    switch (requirementItemType) {
      case InstantAccessRequirementItem_Type.ORDERS_INTEGRATION_SETUP:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.integrationSetup.label', { platformLabel }),
          ctaLabel: t('bankingInstantPayouts.checklistAccordion.requirementItems.integrationSetup.ctaLabel'),
        };
      case InstantAccessRequirementItem_Type.ACTIVE_CHECKING_ACCOUNT:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.label'),
          ctaLabel: isMobile ?
            t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.ctaLabel.mobile')
            : t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.ctaLabel.desktop'),
        };
      case InstantAccessRequirementItem_Type.DIRECT_DEPOSITS_SETUP:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.label'),
          ctaLabel: isMobile ?
            t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.ctaLabel.mobile')
            : t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.ctaLabel.desktop'),
        };
      default:
        return {};
    }
  };

  // render functions
  const renderStaticAccordionItem = (item: InstantAccessAccordionItem) => (
    <div key={item.platformLabel} className={styles.checklistAccordionWrapper}>
      <MarketRow
        data-testid="static-checklist-accordion"
        className={styles.staticChecklistAccordionItem}
        size="small"
      >
        <MarketAccessory size="image" slot="leading-accessory">
          <img
            className={styles.checklistAccordionLabelLogo}
            src={item.logoImageUrl}
            alt={item.platformLabel}
            style={{ backgroundColor: item.logoImageBackgroundColorCode }}
          />
        </MarketAccessory>
        <label slot="label"><h3>{item.platformLabel}</h3></label>
        <MarketPill
          className={styles.checklistAccordionLabelPill}
          size="medium"
          slot="trailing-accessory"
          variant={getPillVariantAndLabel(item.pillDescription?.variant).variant as MarketPillVariant}
        >
          {getPillVariantAndLabel(item.pillDescription?.variant).label}
        </MarketPill>
      </MarketRow>
    </div>
  );

  const renderRequirementRowSvg = (type?: InstantAccessRequirementItem_Type) => {
    switch (type) {
      case InstantAccessRequirementItem_Type.ACTIVE_CHECKING_ACCOUNT:
        return <BankSvg />;
      case InstantAccessRequirementItem_Type.DIRECT_DEPOSITS_SETUP:
        return <SignDollarArrowSvg />;
      case InstantAccessRequirementItem_Type.ORDERS_INTEGRATION_SETUP:
        return <DualRotatingArrowSvg />;
      default:
        return null;
    }
  };

  const renderRequirementRows = (item: InstantAccessAccordionItem) => (
    item.requirementItems?.map((reqItem) => {
      const { label, ctaLabel } = getRequirementItemLabelAndCTALabel(reqItem.type, item.platformLabel);

      return (<MarketRow
        key={label}
        transient
        data-testid="checklist-accordion-requirement-row"
        className={styles.checklistAccordionRow}
      >
        <MarketAccessory size="icon" slot="leading-accessory">
          {renderRequirementRowSvg(reqItem.type)}
        </MarketAccessory>

        <label slot="label">
          {label}
        </label>

        <MarketButton
          data-testid="checklist-accordion-requirement-row-cta"
          slot="trailing-accessory"
          disabled={!!reqItem.isDisabled}
          onClick={() => onRequirementItemClick(reqItem, item)}
        >
          {ctaLabel}
        </MarketButton>
      </MarketRow>);
    })
  );

  const renderDynamicAccordionItem = (item: InstantAccessAccordionItem) => {
    // when there is only one requirement item, we auto expand the accordion
    const shouldAutoExpandedAccordion = item.requirementItems.length === 1;

    return (<div key={item.platformLabel} className={styles.checklistAccordionWrapper}>
      <MarketAccordionItem
        className={styles.checklistAccordionItem}
        data-testid="checklist-accordion"
        name={item.platformLabel || ''}
        disabled={!!item.isDisabled}
        expanded={shouldAutoExpandedAccordion}
      >
        <span slot="label" className={styles.checklistAccordionLabel}>
          <span className={styles.checklistAccordionLabelTitle}>
            <img
              className={styles.checklistAccordionLabelLogo}
              src={item.logoImageUrl}
              alt={item.platformLabel}
              style={{ backgroundColor: item.logoImageBackgroundColorCode }}
            />
            <label>{item.platformLabel}</label>
          </span>
          <MarketPill
            className={styles.checklistAccordionLabelPill}
            size="medium"
            variant={getPillVariantAndLabel(item.pillDescription?.variant).variant as MarketPillVariant}
          >
            {getPillVariantAndLabel(item.pillDescription?.variant).label}
          </MarketPill>
        </span>

        <div>
          {renderRequirementRows(item)}
        </div>
      </MarketAccordionItem>
    </div>);
  };

  return (
    <div className={styles.checklistAccordionContainer}>
      {instantAccessAccordionItems.map((item) => {
        // When accordion is already completed, we show a static row as a mimic accordion
        // otherwise, show a real dynamic accordion
        const isAccordionCompleted = item.pillDescription?.variant === PillDescription_PillVariant.PILL_VARIANT_SUCCESS;

        return isAccordionCompleted
          ? renderStaticAccordionItem(item)
          : renderDynamicAccordionItem(item);
      })}
    </div>
  );
};

export const ChecklistAccordionI18n = withI18n(ChecklistAccordion);
